import { useState } from "react";
import { motion } from "framer-motion";
import { Avatar } from "@/components/User/Avatar";
import { MentionParser } from "@/components/Home/MentionParser";
import { formatDistanceToNow } from "date-fns";
import { Reply, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type Comment = {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  parent_id?: string | null;
  profiles: {
    username: string;
    avatar_id: number | null;
  };
  replies?: Comment[];
};

interface CommentItemProps {
  comment: Comment;
  index?: number;
  onReply?: (commentId: string, username: string) => void;
  isReply?: boolean;
}

export function CommentItem({ comment, index = 0, onReply, isReply = false }: CommentItemProps) {
  const [showReplies, setShowReplies] = useState(true);
  const hasReplies = comment.replies && comment.replies.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className={cn(
        "py-3",
        isReply && "ml-10 border-l-2 border-brand-cyan/20 pl-4"
      )}
    >
      <div className="flex gap-3 hover:bg-white/5 rounded-lg px-2 py-2 -mx-2 transition-colors">
        <Avatar
          userId={comment.user_id}
          avatarId={comment.profiles?.avatar_id || 1}
          size={isReply ? "sm" : "sm"}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm text-white/90">
              @{comment.profiles?.username || "Unknown User"}
            </span>
            <span className="text-xs text-white/40">
              {formatDistanceToNow(new Date(comment.created_at), {
                addSuffix: true,
              })}
            </span>
          </div>
          <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap break-words">
            <MentionParser text={comment.content} />
          </p>
          
          {/* Reply button */}
          {!isReply && onReply && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onReply(comment.id, comment.profiles?.username || "user")}
              className="mt-1 h-7 px-2 text-xs text-white/40 hover:text-brand-cyan hover:bg-brand-cyan/10"
            >
              <Reply className="h-3 w-3 mr-1" />
              Reply
            </Button>
          )}
        </div>
      </div>

      {/* Replies */}
      {hasReplies && (
        <div className="mt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowReplies(!showReplies)}
            className="h-6 px-2 text-xs text-brand-cyan/70 hover:text-brand-cyan hover:bg-brand-cyan/10"
          >
            {showReplies ? (
              <>
                <ChevronUp className="h-3 w-3 mr-1" />
                Hide {comment.replies!.length} {comment.replies!.length === 1 ? "reply" : "replies"}
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3 mr-1" />
                Show {comment.replies!.length} {comment.replies!.length === 1 ? "reply" : "replies"}
              </>
            )}
          </Button>
          
          {showReplies && (
            <div className="mt-2">
              {comment.replies!.map((reply, replyIndex) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  index={replyIndex}
                  isReply={true}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
